import type { TranslationKeys } from './en';

export const uk: TranslationKeys = {
  // Common
  common: {
    loading: 'Завантаження...',
    saving: 'Збереження...',
    back: 'Назад',
    show: 'Показати',
    hide: 'Сховати',
    day: {
      monday: 'Понеділок',
      tuesday: 'Вівторок',
      wednesday: 'Середа',
      thursday: 'Четвер',
      friday: 'П\'ятниця',
      saturday: 'Субота',
      sunday: 'Неділя',
    },
    dayAbbr: {
      mon: 'Пн',
      tue: 'Вт',
      wed: 'Ср',
      thu: 'Чт',
      fri: 'Пт',
      sat: 'Сб',
      sun: 'Нд',
    },
    month: {
      january: 'Січень',
      february: 'Лютий',
      march: 'Березень',
      april: 'Квітень',
      may: 'Травень',
      june: 'Червень',
      july: 'Липень',
      august: 'Серпень',
      september: 'Вересень',
      october: 'Жовтень',
      november: 'Листопад',
      december: 'Грудень',
    },
    relativeDates: {
      today: 'Сьогодні',
      tomorrow: 'Завтра',
      onDay: '{{day}}',
      inDays: 'Через {{count}}',
      inWeeks: 'Через {{count}}',
      days1: 'День',
      days2_4: 'Дні',
      days5plus: 'Днів',
      weeks1: 'Тиждень',
      weeks2_4: 'Тижні',
      weeks5plus: 'Тижнів',
    },
    errors: {
      networkError: 'Помилка мережі. Перевірте підключення та спробуйте знову.',
      serverError: 'Помилка сервера. Спробуйте пізніше.',
      unexpected: 'Щось пішло не так. Спробуйте пізніше.',
    },
  },

  // User Relation Statuses
  userRelationStatus: {
    ACTIVE: 'Активний',
    INACTIVE: 'Неактивний',
    PENDING_APPROVAL_BY_TRAINEE: 'Очікування підтвердження клієнта',
    PENDING_APPROVAL_BY_COACH: 'Очікування підтвердження тренера',
    DECLINED_BY_TRAINEE: 'Відхилено клієнтом',
    DECLINED_BY_COACH: 'Відхилено тренером',
  },

  // Terminology
  terms: {
    provider: 'Тренер',
    providers: 'Тренери',
    providerPossessive: 'Тренера',
    myProvider: 'Мій тренер',
    myProviders: 'Мої тренери',
    receiver: 'Клієнт',
    receivers: 'Клієнти',
    receiverPossessive: 'Клієнта',
    myReceiver: 'Мій клієнт',
    myReceivers: 'Мої клієнти',
    session: 'Тренування',
    sessions: 'Тренування',
    sessionSet: 'Абонемент',
    sessionSets: 'Абонементи',
    sessionTemplate: 'Шаблон абонементу',
    sessionTemplates: 'Шаблони абонементів',
    createSession: 'Створити тренування',
    editSession: 'Редагувати тренування',
    viewSession: 'Переглянути тренування',
    completeSession: 'Завершити тренування',
    scheduleSession: 'Запланувати тренування',
  },

  // Training Set Statuses
  trainingSetStatus: {
    DRAFT: 'Чернетка',
    ACTIVE: 'Активний',
    COMPLETED: 'Завершено',
    CANCELLED: 'Скасовано',
  },

  // Training Projection Statuses
  trainingStatus: {
    SCHEDULED: 'Заплановано',
    COMPLETED: 'Завершено',
    MOVED: 'Перенесено',
    SKIPPED: 'Пропущено',
    CANCELLED: 'Скасовано',
    ACTIVE: 'Активне',
    UPCOMING: 'Майбутнє',
  },

  dashboard: {
    welcome: 'Вітаємо, {{name}}!',
    subtitle: 'Ваша панель керування тренуваннями',
    usernameWarning: {
      title: 'Додайте ім’я користувача Telegram',
      message: 'Тренерам потрібен ваш @username для запрошень. Встановіть його один раз у налаштуваннях Telegram.',
      details: 'Telegram → Налаштування → Ім’я користувача, потім поверніться сюди.',
      cta: 'Відкрити налаштування Telegram',
      showMore: 'Детальніше',
      showLess: 'Сховати',
    },
    overLimitWarning: {
      title: 'Перевищено ліміт підписки',
      upgrade: 'Розблокувати',
      traineesExceeded: 'Ви досягли ліміту клієнтів',
    },
    quickActions: {
      title: 'Швидкі дії',
      generateReports: 'Створити звіти',
      generateReportsDesc: 'Переглянути аналітику та прогрес',
      inviteTrainee: 'Запросити клієнта',
      inviteTraineeDesc: 'Надіслати запрошення зареєстрованому користувачу',
      createVirtualTrainee: 'Створити віртуального клієнта',
      createVirtualTraineeDesc: 'Додати клієнта без telegram',
      viewAllTrainees: 'Переглянути всіх клієнтів',
      viewAllTraineesDesc: 'Керувати своїми клієнтами',
    },
    recentTrainees: {
      joined: 'Приєднався',
      title: 'Недавні клієнти',
      viewAll: 'Переглянути всіх',
    },
    emptyState: {
      title: 'Ще немає клієнтів',
      description: 'Почніть із запрошення клієнта або створення віртуального клієнта',
      action: 'Додати першого клієнта',
    },
    stats: {
      activeTrainees: 'Активні клієнти',
      completedSessions: 'Завершені тренування',
      totalSessions: 'Всього тренувань',
    },
    thisWeekTrainings: 'Тренування цього тижня',
    todayTrainings: 'Розклад клієнтів сьогодні',
    nextTraining: 'Наступне тренування',
    myCoaches: {
      title: 'Мої тренери',
      description: 'Переглянути тренерів, з якими ви співпрацюєте',
    },
    greetings: {
      morning: 'Доброго ранку',
      afternoon: 'Добрий день',
      evening: 'Добрий вечір',
    },
    hero: {
      todayOverview: 'Огляд на сьогодні',
      sessionsSuffix: 'тренувань заплановано',
      thisWeekChip: '{{count}} цього тижня',
      activeTraineesChip: '{{count}} активних клієнтів',
    },
    completableAlert: {
      title_one: '{{count}} абонемент готовий до завершення',
      title_other: '{{count}} абонементи готові до завершення',
      subtitle: 'Натисніть, щоб переглянути та завершити',
    },
    traineesSection: {
      title: 'Клієнти',
      seeAll: 'Всі',
      invite: 'Запросити',
    },
    assistantBanner: {
      title: 'Є питання щодо застосунку?',
      subtitle: 'Запитайте нашого асистента — він знає все про Training Tracker.',
      cta: 'Запитати асистента',
    },
  },

  // Trainees List
  trainees: {
    virtualBadge: 'Віртуальний',
    filters: {
      all: 'Всі',
      active: 'Активні',
      pendingByCoach: 'Очікують підтвердження',
      pendingByTrainee: 'Очікують підтвердження',
      declined: 'Відхилені',
    },
    sessions: 'Тренувань',
    joined: 'Приєднався',
    emptySearch: {
      tryDifferent: 'Спробуйте інший запит',
      title: 'Клієнтів не знайдено',
      startInviting: 'Почніть із запрошення клієнтів!',
    },
    title: 'Клієнти',
    subtitle: 'Керуйте своїми клієнтами та їхніми абонементами',
    addButton: 'Додати клієнта',
    inviteButton: 'Запросити клієнта',
    searchPlaceholder: 'Пошук клієнтів...',
    total: 'Всього клієнтів: {{count}}',
  },

  // Contacts
  contacts: {
    title: 'Контакти',
    total: '{{total}} всього ({{coaches}} тренерів, {{clients}} клієнтів)',
    searchPlaceholder: 'Пошук контактів...',
    filters: {
      all: 'Всі',
      coaches: 'Тренери',
      clients: 'Клієнти',
    },
    label: {
      coach: 'Тренер',
      client: 'Клієнт',
    },
    emptySearch: {
      title: 'Контакти не знайдено',
      tryDifferent: 'Спробуйте інший запит',
      noContacts: 'У вас ще немає контактів',
    },
  },

  // Invite Trainee
  invite: {
    title: 'Запросити нового клієнта',
    subtitle: 'Надішліть запрошення зареєстрованому клієнту',
    info: {
      title: 'Як запросити',
      step1: '1. Запитайте в клієнта його Telegram username',
      step2: '2. Переконайтеся, що клієнт зареєстрований у цьому боті',
      step3: '3. Введіть його username нижче (без @)',
      step4: '4. Клієнт отримає запрошення і зможе прийняти або відхилити його',
      step5: '5. Ви отримаєте сповіщення в Telegram, коли клієнт відповість',
    },
    form: {
      username: 'Telegram username',
      usernameRequired: 'Telegram username *',
      usernamePlaceholder: 'Username',
      usernameHelp: 'Введіть username без символу @',
      noTelegram: 'Клієнт не використовує Telegram?',
    },
    button: {
      submit: 'Надіслати запрошення',
      submitting: 'Надсилання...',
      success: 'Запрошення надіслано!',
    },
    errors: {
      userNotFound: 'Користувача з іменем "{{username}}" не знайдено. Переконайтеся, що він зареєстрований у боті.',
      alreadyInvited: 'Цей користувач вже запрошений.',
      selfInvite: '😅 гарна спроба, але ви не можете запросити самого себе.',
      unknown: 'Не вдалося надіслати запрошення. Спробуйте ще раз.',
    },
    limitReached: {
      title: 'Більше клієнтів додати не можна',
      body: 'На поточному плані досягнуто ліміт клієнтів. Розблокуйте Pro, щоб запрошувати більше.',
    },
  },

  // Create Virtual Trainee
  virtualTrainee: {
    title: 'Створити віртуального клієнта',
    subtitle: 'Додати клієнта без облікового запису telegram',
    info: {
      title: 'Про віртуальних клієнтів',
      description: 'Віртуальні клієнти призначені для відстеження людей, які не використовують Telegram. Вони не отримуватимуть сповіщення, але ви можете керувати їхніми тренуваннями як зазвичай.',
    },
    form: {
      firstNameRequired: 'Ім\'я *',
      firstNamePlaceholder: 'Іван',
      lastName: 'Прізвище (необов\'язково)',
      lastNamePlaceholder: 'Петренко',
      lastNameHelp: 'Прізвище необов\'язкове',
    },
    button: {
      submit: 'Створити віртуального клієнта',
      submitting: 'Створення...',
      success: 'Віртуального клієнта створено!',
    },
    errors: {
      invalidData: 'Будь ласка, вкажіть коректне ім\'я (2-255 символів)',
      unknown: 'Не вдалося створити віртуального клієнта. Спробуйте ще раз.',
    },
  },

  // Reports
  reports: {
    title: 'Звіти',
    subtitle: 'Аналітика та прогрес',
    comingSoon: {
      title: 'Скоро буде',
      description: 'Аналітика та звіти',
    },
  },

  // All Training Sets
  allTrainingSets: {
    title: 'Всі абонементи',
    subtitle: 'Переглянути всі ваші абонементи',
    canComplete: 'Можна завершити',
    unpaid: 'Не оплачено',
    filters: {
      all: 'Всі',
      canBeCompleted: 'Завершити',
      unpaid: 'Неоплачені',
    },
    emptyState: {
      title: 'Немає абонементів',
      description: 'У вас поки немає абонементів',
    },
    error: {
      title: 'Помилка',
      tryAgain: 'Спробувати знову',
    },
  },

  activeTrainingSet: {
    waitingTitle: 'Ще не призначено',
    waitingBody: 'Тренер незабаром призначить абонемент.',
  },

  // Complete Set Modal
  modal: {
    complete: {
      title: 'Завершити абонемент',
      description: 'Ви впевнені, що хочете завершити цей абонемент?',
      autoProlongation: {
        label: 'Авто-продовження',
        description: 'Створити новий абонемент з наступного дня після завершення цього абонемента',
      },
      cancel: 'Скасувати',
      confirm: 'Завершити',
    },
    pause: {
      title: 'Призупинити абонемент',
      startDate: 'Дата початку паузи',
      endDate: 'Дата закінчення паузи',
      cancel: 'Скасувати',
      confirm: 'Призупинити',
      errors: {
        startDateInPast: 'Дата початку повинна бути в майбутньому',
        endDateInPast: 'Дата закінчення повинна бути в майбутньому',
        endDateBeforeStart: 'Дата закінчення повинна бути після дати початку',
      },
    },
  },

  // Edit Training
  editTraining: {
    title: 'Редагувати тренування',
    form: {
      name: 'Час початку',
      status: 'Статус',
      statusOptions: {
        scheduled: 'Заплановано',
        active: 'Активне',
        completed: 'Завершено',
        skipped: 'Пропущено',
        moved: 'Перенесено',
        cancelled: 'Скасовано',
      },
      date: 'Дата',
      startTime: 'Час початку',
      duration: 'Тривалість',
      hours: 'Години',
      minutes: 'Хвилини',
      notes: 'Коментар / причина оновлення',
      notesPlaceholder: 'Додайте коментар або причину для цього оновлення...',
      notesVisibleToTrainee: 'Цей коментар буде видимий для клієнта',
      markAsFired: 'Позначити як списане 🔥',
      firedDescription: 'Списані тренування враховуються, навіть якщо клієнт не відвідав їх',
    },
    button: {
      save: 'Зберегти зміни',
      saving: 'Збереження...',
      cancel: 'Скасувати',
    },
    errors: {
      loadFailed: 'Не вдалося завантажити тренування',
      updateFailed: 'Не вдалося оновити тренування',
    },
    success: {
      updated: 'Тренування успішно оновлено!',
    },
    skipLimit: {
      willBeSkipped: '{{planned}}/{{max}} тренувань буде пропущено',
      countOnly: 'Пропущених тренувань: {{current}}',
    },
    notificationInfo: 'Клієнт отримає сповіщення про це редагування з усіма деталями',
  },

  // My Coaches
  myCoaches: {
    error: {
      title: 'Помилка',
      tryAgain: 'Спробувати знову',
    },
    emptyState: {
      title: 'Поки немає тренерів',
      description: 'У вас ще немає жодного тренера',
    },
    card: {
      trainingSets: 'Пакети тренувань',
    },
  },

  // User Detail
  userDetail: {
    backToTrainees: 'Назад до списку клієнтів',
    backToCoaches: 'Назад до моїх тренерів',
    openChat: 'Відкрити чат',
    openChatNoUsername: 'У цього користувача не вказано ім\'я в Telegram.',
    virtualBadge: 'Віртуальний',
    error: {
      title: 'Помилка',
      back: 'Назад до {{type}}',
    },
    edit: {
      button: 'Редагувати імʼя',
      title: 'Редагувати віртуального клієнта',
      save: 'Зберегти зміни',
      saving: 'Збереження...',
      cancel: 'Скасувати',
    },
    delete: {
      buttonSpecialist: 'Видалити тренера',
      buttonClient: 'Видалити клієнта',
      deleting: 'Видалення...',
      confirmTitle: '⚠️ важливе попередження ⚠️',
      confirmMessage: 'Ви впевнені, що хочете видалити цього {{type}}?',
      confirmDetails: '🗑️ це назавжди видалить:\n• відносини\n• призначену тренувальну програму (якщо є)\n• всі пакети подій\n• всі пов\'язані дані\n\n❌ цю дію не можна скасувати, і всі дані будуть втрачені назавжди!',
      errorTitle: '❌ не вдалося видалити {{type}}:',
      errorRetry: 'Будь ласка, спробуйте знову.',
      typeSpecialist: 'тренера',
      typeClient: 'клієнта',
    },
    invite: {
      accept: 'Прийняти',
      decline: 'Відхилити',
      accepting: 'Приймаю...',
      declining: 'Відхиляю...',
      acceptConfirm: 'Прийняти запрошення',
      soloCancelWarning: 'Приєднання до тренера скасує вашу Solo-підписку. Далі функції залежатимуть від плану тренера.',
      declineConfirm: 'Відхилити запрошення',
      acceptError: '❌ не вдалося прийняти запрошення',
      declineError: '❌ не вдалося відхилити запрошення',
    },
    openHistory: 'Історія',
    openHistoryHint: 'Абонементи, програми та тренування',
    programs: {
      title: 'Тренувальна програма',
      assignButton: 'Призначити програму',
      changeButton: 'Змінити програму',
      noAssignment: 'Програму ще не призначено',
      ongoing: 'Активна',
      historyTitle: 'Історія програм',
      noHistory: 'Попередніх програм немає',
      reassignButton: 'Призначити знову',
      saveToLibrary: 'Додати до збережених',
      unsavedRetentionHint: 'Не збережені програми старші за 3 місяці видаляються з історії. Додайте їх до збережених, щоб залишити.',
      durationLabel: 'Тривалість',
      periodLabel: 'Період',
      unsavedBadge: 'Не збережена',
      deassignAria: 'Зняти призначення програми',
    },
    trainingSets: {
      title: 'Активний абонемент',
      noSets: 'Поки немає абонементів',
      hasActiveSet: 'У цього клієнта вже є активний абонемент. Будь ласка, завершіть або скасуйте існуючий абонемент перед створенням нового.',
      createNew: 'Створити новий',
      cannotCreate: {
        title: 'Неможливо створити абонемент',
        description: 'Абонементи можна створювати лише для клієнтів з активним статусом.',
      },
      trainingDays: 'Тренувальні дні',
      plannedTrainings: 'Заплановано тренувань:',
      status: 'Статус',
      startDate: 'Дата початку',
      endDate: 'Дата закінчення',
      sessionDuration: '/сесія',
      sessionsLabel: 'Занять',
      scheduleLabel: 'Розклад',
      paused: 'На паузі',
      deleteAria: 'Видалити абонемент',
    },
    history: {
      title: 'Історія абонементів',
      noHistory: 'Ще немає завершених абонементів',
      retentionHint: 'Завершені абонементи старші за 3 місяці видаляються з історії.',
    },
  },

  // Create Training Set
  createTrainingSet: {
    title: 'Створити абонемент',
    subtitle: 'Для {{trainee}}',
    useTemplate: 'Використати шаблон',
    tabs: {
      trainings: 'Тренування',
    },
    form: {
      name: 'Назва абонемента',
      trainingDays: 'Дні тренувань',
      trainingDaysRequired: 'Дні тренувань *',
      plannedTrainings: 'Заплановані тренування',
      addTraining: 'Додати тренування',
      trainingName: 'Назва тренування',
      removeTraining: 'Видалити',
      maxSkipCountHint: 'Максимальна кількість пропущених тренувань (0-365, не може перевищувати заплановані тренування)',
      defaultStartTime: 'Час початку за замовчуванням',
      defaultStartTimeHelp: 'Час за замовчуванням для всіх днів тренувань',
      hours: 'Години',
      minutes: 'Хвилини',
      sessionDuration: 'Тривалість тренування',
      startDate: 'Дата початку',
      maxSkipCount: 'Макс. кількість пропусків',
      maxSkipCountPlaceholder: 'Напр., 3',
    },
    errors: {
      previewFailed: 'Не вдалося завантажити попередній перегляд',
      startDateRequired: 'Дата початку обов\'язкова',
      nameRequired: 'Назва абонемента обов\'язкова',
      noTrainings: 'Необхідно додати принаймні одне тренування',
      trainingNameRequired: 'Назва тренування обов\'язкова',
      createFailed: 'Не вдалося створити абонемент',
      noDaysSelected: 'Будь ласка, виберіть принаймні один день для тренувань',
      hasActiveSet: 'У цього клієнта вже є активний абонемент. Будь ласка, завершіть або скасуйте існуючий абонемент перед створенням нового.',
      traineeLimit: 'Ви перевищили ліміт клієнтів. Будь ласка, оновіть свій план або видаліть деяких клієнтів, щоб створювати абонементи.',
      durationLimit: 'Тривалість абонемента становить {current} днів, але ваш план дозволяє максимум {max} днів. Будь ласка, зменшіть кількість тренувань або оновіть свій план.',
    },
    button: {
      backToEdit: 'Назад до редагування',
      cannotCreate: 'Неможливо створити',
      creating: 'Створення...',
      create: 'Створити абонемент',
      preview: 'Попередній перегляд розкладу',
    },
    messages: {
      createSuccess: 'Абонемент успішно створено!',
    },
    success: {
      createSuccess: 'Абонемент успішно створено!',
    },
    preview: {
      title: 'Попередній перегляд розкладу тренувань',
      subtitle: 'Заплановано {{count}} тренувань',
      durationExceeded: 'Перевищено ліміт тривалості',
      first: 'Перше',
      last: 'Останнє',
      loadingPreview: 'Завантаження попереднього перегляду...',
      overlapWarning: {
        description: 'Цей абонемент перетинається з існуючим активним абонементом для цього клієнта. Будь ласка, змініть дати або завершіть/скасуйте існуючий абонемент перед створенням нового.',
        title: 'Попередження про перетин',
      },
      session: 'Сесія',
      week: 'Тиждень',
    },
    modal: {
      overlap: {
        description: 'Цей абонемент перетинається з існуючим активним абонементом для цього клієнта. Будь ласка, змініть дати або завершіть/скасуйте існуючий абонемент перед створенням нового.',
      },
    },
    templateCard: {
      hours: 'Год',
      minutes: 'Хв',
      trainings: 'Тренувань',
    },
  },

  // Training Set Detail (generic parts only - progress, actions, messages, errors, schedule)
  trainingSetDetail: {
    title: 'Деталі абонемента',
    subtitle: '{{count}} тренувань',
    loading: 'Завантаження тренувань...',
    relatedUser: {
      coachLabel: 'Тренер',
      traineeLabel: 'Клієнт',
    },
    progress: {
      title: 'Прогрес',
      planned: 'Заплановано',
      completed: 'Завершено',
      remaining: 'Залишилось',
      fired: 'Списано',
      outOf: 'З',
      skippedCancelled: 'Пропущено/скасовано',
      notCounted: 'Не враховується',
    },
    actions: {
      getReport: 'Отримати звіт',
      sendingReport: 'Відправка звіту...',
    },
    messages: {
      reportSent: 'Звіт успішно відправлено!',
      deleteSuccess: 'Абонемент успішно видалено!',
      completeSuccess: 'Абонемент успішно завершено!',
      completeWithProlongation: 'Абонемент успішно завершено! Новий абонемент створено з наступного дня після завершення цього абонемента.',
      pauseSuccess: 'Абонемент успішно призупинено!',
      resumeSuccess: 'Абонемент успішно відновлено!',
      cancelPauseSuccess: 'Паузу успішно скасовано!',
      markUnpaidSuccess: 'Позначено як неоплачений',
      clearUnpaidSuccess: 'Позначено як оплачений',
    },
    errors: {
      reportFailed: 'Не вдалося відправити звіт',
      pauseFailed: 'Не вдалося призупинити абонемент',
      resumeFailed: 'Не вдалося відновити абонемент',
      cancelPauseFailed: 'Не вдалося скасувати паузу',
      deleteFailed: 'Не вдалося видалити абонемент',
      completeFailed: 'Не вдалося завершити абонемент',
      unpaidFailed: 'Не вдалося оновити позначку неоплати',
    },
    confirmations: {
      resume: 'Ви впевнені, що хочете відновити цей абонемент?',
      cancelPause: 'Ви впевнені, що хочете скасувати паузу?',
      delete: 'Ви впевнені, що хочете видалити цей абонемент? Цю дію не можна скасувати.',
      complete: 'Завершити абонемент?',
    },
    pause: {
      title: 'Цей абонемент має період паузи',
      period: 'Період паузи',
    },
    schedule: {
      title: 'Розклад тренувань',
      week: 'Тиждень',
      comment: 'Коментар',
      first: 'Перше',
      last: 'Останнє',
      next: 'Наступне',
      session: 'Подія',
      pastTrainings: 'Минулі тренування',
      training: 'Тренування',
      trainings: 'Тренувань',
      emptyWeek: 'Немає тренувань на цей тиждень',
    },
    stats: {
      nextTraining: 'Наступне тренування',
    },
    emptyState: {
      title: 'Абонемент не знайдено',
    },
    empty: {
      title: 'Тренування для цього абонемента не знайдено',
      description: 'У цьому абонементі немає запланованих тренувань',
    },
  },

  // Training Set Actions (generic button labels)
  trainingSetActions: {
    complete: 'Завершити',
    completing: 'Завершення...',
    delete: 'Видалити',
    deleting: 'Видалення...',
    pause: 'Призупинити',
    pausing: 'Призупинення...',
    resume: 'Відновити',
    resuming: 'Відновлення...',
    cancelPause: 'Скасувати паузу',
    cancelingPause: 'Скасування...',
    report: 'Звіт',
    markUnpaid: 'Не оплачено',
    clearUnpaid: 'Оплачено',
    updatingUnpaid: 'Оновлення...',
  },

  // Training Set Stats (generic labels)
  trainingSetStats: {
    cancelled: 'Скасовано',
    completed: 'Завершено',
    fired: 'Списано',
    left: 'Залишилось',
    passed: 'Пройдено',
    planned: 'Заплановано',
    remaining: 'Залишилось',
    skipped: 'Пропущено',
    nextTraining: 'Наступне тренування',
    totalTrainings: 'Всього тренувань',
    firedTrainings: 'Списаних тренувань',
  },

  // Training Templates
  trainingTemplates: {
    title: 'Шаблони абонементів',
    subtitle: 'Створюйте шаблони для повторного використання',
    backToPreferences: 'Назад до налаштувань',
    createNew: 'Створити новий шаблон',
    edit: 'Редагувати',
    copy: 'Копіювати',
    delete: 'Видалити',
    confirmDelete: 'Ви впевнені, що хочете видалити цей шаблон?',
    copyConfirm: 'Копіювати шаблон "{{name}}"?',
    copyPrefix: 'Копія',
    copyFailed: 'Не вдалося скопіювати шаблон',
    trainings: 'Тренувань',
    maxSkip: 'Макс. пропущено',
    noDescription: 'Без опису',
    empty: {
      title: 'Поки немає шаблонів',
      description: 'Створіть свій перший шаблон для повторного використання типових тренувань',
    },
  },

  // Template Form
  templateForm: {
    createTitle: 'Створити шаблон',
    editTitle: 'Редагувати шаблон',
    subtitle: 'Заповніть властивості, які хочете використовувати повторно',
    sections: {
      details: 'Деталі',
      schedule: 'Розклад',
      goals: 'Цілі',
      skipRules: 'Правила пропусків',
    },
    daysPerWeek: 'днів / тиждень',
    tapToAdjustTime: 'Натисніть для налаштування часу',
    noTrainingDays: {
      title: 'Дні тренувань не вибрані',
      hint: 'Натисніть на день вище, щоб додати його до розкладу',
    },
    durationUnits: {
      hours: 'год',
      minutes: 'хв',
    },
    fields: {
      name: 'Назва шаблону',
      description: 'Опис',
      days: 'Дні подій',
      defaultStartTime: 'Час початку за замовчуванням',
      plannedTrainings: 'Заплановані тренування',
      duration: 'Тривалість тренування',
      maxSkipCount: 'Макс. пропущених',
    },
    placeholders: {
      name: 'Напр., стандартний тиждень тренувань',
      description: 'Опціональний опис',
      plannedTrainings: 'Напр., 12',
      maxSkipCount: 'Напр., 3',
    },
    hints: {
      defaultStartTime: 'Час за замовчуванням для всіх днів тренувань',
      plannedTrainings: 'Кількість тренувань (1-365)',
      duration: 'Максимум 12 годин',
      maxSkipCount: 'Максимальна кількість пропущених тренувань (0-365, не може перевищувати заплановані)',
    },
    actions: {
      create: 'Створити шаблон',
      update: 'Оновити шаблон',
    },
    errors: {
      nameRequired: 'Назва шаблону обов\'язкова',
      nameTooLong: 'Назва має бути не більше 100 символів',
      descriptionTooLong: 'Опис має бути не більше 255 символів',
      noPropertiesSet: 'Принаймні одна властивість шаблону має бути встановлена',
      plannedTrainingsRange: 'Заплановані тренування мають бути від 1 до 365',
      durationTooLong: 'Тривалість не може перевищувати 12 годин',
      maxSkipCountRange: 'Макс. пропущених має бути від 0 до 365',
      maxSkipExceedsPlanned: 'Макс. пропущених не може перевищувати заплановані тренування',
    },
  },

  // Тренування цього тижня
  thisWeekTrainings: {
    weekView: 'Тиждень',
    dayView: 'День',
    sameTime: 'Однаковий час',
    title: 'Розклад клієнтів цього тижня',
    subtitle: 'Всі заплановані тренування на поточний тиждень',
    todayTitle: 'Розклад клієнтів сьогодні',
    todaySubtitle: 'Всі тренування заплановані на сьогодні',
    emptyTitle: 'Немає тренувань цього тижня',
    emptyMessage: 'На цей тиждень не заплановано жодного тренування',
    emptyTodayTitle: 'Немає тренувань сьогодні',
    emptyTodayMessage: 'На сьогодні не заплановано жодного тренування',
    nextTraining: 'Наступне тренування',
    asCoach: 'Як тренер',
    asTrainee: 'Як клієнт',
  },

  // Pro View
  proView: {
    title: 'Pro View',
    bannerHint: 'Зручний огляд для тренуваннь з декількома клієнтами!',
    emptyMessage: 'Сьогодні не заплановано жодного тренування',
    noProgram: 'Програму не призначено',
    noProgramMessage: 'У цього клієнта немає активної програми тренувань',
  },

  proViewLive: {
    title: 'Pro View',
    subtitle: 'Слідкуйте та редагуйте живі тренування клієнтів на сьогодні',
    liveBadge: 'Зараз',
    startWorkout: 'Почати тренування',
    start: 'Старт',
    starting: 'Запуск…',
    moreBlocks: 'Ще {{count}}',
    unplanned: {
      columnTitle: 'Позаплановий клієнт',
      columnHint: 'Почати тренування без запису',
      timeLabel: 'Позапланові',
      pickerTitle: 'Оберіть клієнта',
      pickerEmpty: 'Немає клієнтів з активною програмою, призначеною вами',
      pickerLoading: 'Завантаження клієнтів…',
      cancel: 'Скасувати',
      alreadyInCarousel: 'Вже на дошці',
      addFailed: 'Не вдалося додати цього клієнта',
    },
    error: {
      title: 'Не вдалося завантажити live-режим',
      message: 'Не вдалося отримати дані живих тренувань',
      startFailed: 'Не вдалося почати тренування для клієнта',
    },
  },

  // Coach Dashboard
  coachDashboard: {
    pendingApprovals: 'Очікують підтвердження',
    totalTrainees: 'Всього клієнтів',
    canBeCompleted: 'Можна завершити',
    activeTrainees: 'Активні клієнти',
    clientsEventSets: 'Абонементи клієнтів',
    preferences: {
      title: 'Налаштування',
      settingsAndTemplates: 'Налаштування',
      description: 'Керуйте своїми налаштуваннями',
    },
  },

  // Trainee Dashboard
  traineeDashboard: {
    subtitle: 'Ваша панель керування',
    nextTraining: 'Наступна подія',
    nextSession: {
      title: 'Наступне тренування',
      view: 'Переглянути',
      noSession: 'Немає запланованих тренувань',
    },
    statsWeek: 'тренувань цього тижня',
    statsCoaches: 'тренери',
    explore: 'Більше можливостей',
    mySets: 'Aбонементи',
    blocks: 'Блоки',
    programs: 'Програми',
    becomeCoach: {
      title: 'Стати тренером',
      description: 'Станьте тренером і починайте запрошувати клієнтів',
    },
    closestTrainingSet: {
      label: 'Завершення найближчого абонемента',
      endsAt: 'Завершується',
      trainingsLeft: 'Залишилось тренувань',
    },
    trainingSetWidget: {
      title: 'Активний абонемент',
      startDate: 'Дата початку',
      endDate: 'Дата закінчення',
    },
    manageTrainees: {
      title: 'Керувати моїми клієнтами',
      description: 'Переглянути та керувати вашими клієнтами',
    },
    viewPrograms: {
      title: 'Переглянути мої абонементи',
      description: 'Подивіться ваш графік тренувань',
    },
    trainingBlocks: {
      title: 'Тренувальні блоки',
      description: 'Блоки тренувань, створені через бота',
    },
    trainingPrograms: {
      title: 'Тренувальні програми',
      description: 'Тижневий розклад на основі блоків',
    },
    emptyState: {
      waitingCoach: {
        badge: 'Абонемент',
        title: 'Очікування тренера',
        subtitle:
          'Тренер уже підключений. Невдовзі він призначить абонемент — тоді тут з’являться дати та прогрес.',
        viewCoaches: 'Мої тренери',
      },
      getStarted: {
        badge: 'Особисті тренування',
        title: 'Готові розпочати тренування?',
        subtitle: 'У вас уже є програми — активуйте одну, щоб бачити прогрес тут.',
        hint: 'Оберіть програму й активуйте її — тут з’являться тиждень і кнопка старту.',
        action: 'Переглянути програми',
        activeProgress: 'Прогрес програми',
        notStarted: 'Не почато',
        noBlocksTitle: 'Створіть перший блок',
        noBlocksSubtitle:
          'Блоки — це шаблони тренувань. Зробіть блок, потім зберіть з них програму й активуйте.',
        noBlocksAction: 'Створити блок',
        noProgramsTitle: 'Зберіть програму',
        noProgramsSubtitle:
          'У вас є блоки — об’єднайте їх у тижневу програму й активуйте для тренувань.',
        noProgramsAction: 'Створити програму',
      },
    },
    activeProgram: {
      title: 'Активна програма',
      weekOf: 'Тиждень {{current}} з {{total}}',
      view: 'Переглянути',
    },
    activeWorkout: {
      subtitle: 'Продовжіть з того місця, де зупинились',
    },
    activeProgramsSection: 'Активні абонементи',
    completedProgramsSection: 'Завершені абонементи',
    trainingProgram: 'Абонемент',
    empty: {
      title: 'Ще немає абонементів',
      description: 'Ваш тренер створить для вас абонементи',
      becomeCoach: 'Стати тренером',
    },
  },

  // Trainee Detail
  traineeDetail: {
    title: 'Деталі клієнта',
    subtitle: 'Переглянути та керувати інформацією про клієнта',
    comingSoon: {
      title: 'Скоро буде',
      description: 'Перегляд та керування деталями клієнта, розкладом та тренуваннями',
    },
  },

  // Become Specialist Flow
  becomeSpecialist: {
    abilities: {
      title: 'Що ви можете робити як тренер',
      subtitle: 'Відкрийте для себе всі потужні функції, доступні вам',
      features: [
        'Створювати та керувати абонементами для ваших клієнтів',
        'Відстежувати прогрес клієнтів',
        'Налаштувати шаблони абонементів',
        'Отримувати сповіщення',
        'Переглядати детальну статистику та звіти',
        'Ефективно керувати кількома клієнтами',
      ],
      continue: 'Продовжити',
    },
    plans: {
      title: 'Доступні плани',
      subtitle: 'Почніть з безкоштовного плану - ви завжди можете оновитися пізніше',
      startWithFree: 'Почніть з безкоштовного плану',
      upgradeLater: 'Ви можете оновити план будь-коли з налаштувань',
      continue: 'Продовжити',
    },
    invite: {
      title: 'Запросіть свого першого клієнта',
      description: 'Щоб завершити перехід до тренера, вам потрібно запросити принаймні одного клієнта. Після того, як ви запросите свого першого клієнта, ви офіційно станете тренером!',
    },
    leaveCoachFirst: 'Ви вже тренуєтесь із тренером. Спочатку залиште його, тоді зможете запрошувати власних клієнтів.',
    goToCoaches: 'До моїх тренерів',
  },

  // Bottom Navigation
  navigation: {
    home: 'Головна',
    eventSets: 'Абонементи',
    clients: 'Клієнти',
    coaches: 'Тренери',
    contacts: 'Контакти',
    settings: 'Налаштування',
    schedule: 'Розклад',
    trainees: 'Клієнти',
    mySets: 'Aбонементи',
    more: 'Більше',
    weekly: 'Розклад',
    sets: 'Абонементи',
    blocks: 'Блоки',
    programs: 'Програми',
    assistant: 'Асистент',
  },

  // More / Explore screen
  more: {
    title: 'Більше',
    sections: {
      clients:          { title: 'Тренування клієнтів' },
      coachTraining:    { title: 'Тренування з тренером' },
      personalTraining: { title: 'Особисті тренування' },
      people:           { title: 'Люди' },
      account:          { title: 'Акаунт' },
    },
  },

  // Auth
  auth: {
    authenticating: 'Автентифікація...',
    pleaseWait: 'Будь ласка, зачекайте',
    authenticationFailed: 'Помилка автентифікації',
    unableToAuthenticate: 'Не вдалося автентифікуватися. Будь ласка, спробуйте перезапустити додаток.',
    retry: 'Повторити',
  },

  // Subscription
  subscription: {
    price: {
      free: 'Безкоштовно',
      perMonth: '/місяць',
    },
    plans: {
      FREE: {
        name: 'Безкоштовний',
        description: 'Ознайомлення з додатком, ведення до 10 клієнтів якщо ви тренер або збереження власних тренувальних блоків і програм якщо тренуєтесь самостійно',
        features: [
          'Створення власних блоків і програм (якщо немає тренера)',
          'До 10 клієнтів',
          'Призначення тренувань клієнтам',
          'Немає режиму тренування, якщо у тренера немає Pro',
        ],
      },
      TRIAL: {
        name: 'Пробний',
        description: '14 днів безкоштовного доступу до повних можливостей тренування',
        features: [
          'Режим тренування',
          'Історія тренувань',
          'ProView - моніторинг живих сесій',
          'Історія тренувань та прогресу клієнтів',
          'Автопродовження абонементів',
          'До 10 клієнтів',
        ],
      },
      SOLO: {
        name: 'Соло',
        description: 'Для тих, хто тренується без тренера — це не план для коучингу',
        features: [
          'Режим тренування',
          'Історія тренувань',
          'Створення власних блоків і програм',
          'Не можна додавати клієнтів — для коучингу потрібен Pro',
        ],
      },
      PRO: {
        name: 'Про',
        description: 'Для тренерів: повний інструментарій, клієнти отримують режим тренування',
        features: [
          'Необмежена кількість клієнтів',
          'Автопродовження абонементів',
          'ProView — моніторинг живих сесій',
          'Історія тренувань клієнтів',
          'Режим тренування для вас і клієнтів',
        ],
      },
    },
    status: {
      ACTIVE: 'Активна',
      CANCELLED: 'Скасована',
      PAST_DUE: 'Помилка оплати',
      TRIALING: 'Пробна',
      INCOMPLETE: 'Незавершена',
    },
    mySubscription: 'Моя підписка',
    subtitle: 'План, доступ і оплата',
    currentPlan: 'Поточний план',
    choosePlan: 'Плани',
    access: {
      title: 'Ваш доступ',
      fromCoach: 'Залежить від плану тренера',
      workoutMode: 'Режим тренування',
      workoutHistory: 'Власна історія тренувань та прогресу',
      createOwn: 'Створення власних тренувальних блоків і програм',
      addTrainee: 'Ведення до 10 клієнтів якщо ви тренер',
      unlimitedClients: 'Необмежена кількість клієнтів',
      proView: 'ProView - зручна робота з декількома клієнтами одночасно',
      clientHistory: 'Історія тренувань та прогресу клієнтів',
      autoProlong: 'Автопродовження абонементів клієнтів',
    },
    eligibility: {
      coachedAccess: 'У вас є тренер, тому режим тренування та історія залежать від його плану. Solo недоступний, поки ви не залишите тренера.',
      coachAccess: 'Pro відкриває моніторинг тренувань, історію клієнтів, автопродовження абонементів та необмежену кількість клієнтів. Ваші клієнти також отримують режим тренування та зможуть бачити історію своїх тренувань.',
      soloAccess: 'Solo відкриває режим тренування та історію для самостійних тренувань. Pro лише для тренерів.',
      soloHiddenWithCoach: 'Solo для самостійних тренувань. Спочатку залиште тренера, щоб обрати його.',
      soloHiddenAsCoach: 'Solo для самостійних тренувань. Якщо ви тренер, ви можете обрати Pro для ведення клієнтів.',
      proHiddenNotCoach: 'Pro лише для тренерів. Спочатку запросіть клієнта, потім оновіть план.',
      leaveCoachToInvite: 'Спочатку залиште тренера, щоб запрошувати клієнтів.',
    },
    clients: 'Клієнти',
    templates: 'Шаблони абонементів',
    upgrade: 'Оновити',
    unlockCta: 'Розблокувати',
    upgradeTo: 'Перейти на {plan}',
    manageBilling: 'Керувати підпискою',
    cancelSubscription: 'Скасувати автопродовження',
    renewsOn: 'Продовжується',
    expiresOn: 'Закінчується',
    cancelledOn: 'Скасовано',
    confirmCancel: 'Скасувати автопродовження? Доступ залишиться до кінця оплаченого періоду.',
    cancelBeforeSwitch: 'Щоб змінити Solo на Pro або навпаки, спочатку скасуйте поточний план і дочекайтеся його закінчення.',
    retry: 'Повторити',
    loadError: 'Не вдалося завантажити підписку',
    manageDescription: 'Керувати планом та підпискою',
    checkoutFailed: 'Оплату не вдалося завершити. Спробуйте ще раз.',
    paidWithStars: 'Безпечна оплата через Telegram Stars',
    trial: {
      title: 'Спробувати всі переваги безкоштовно на 14 днів',
      description: 'Відкрийте режим тренування, історію, ProView, історію клієнтів та автопродовження на 14 днів безкоштовно.',
      activate: 'Активувати безкоштовний період',
      activateFailed: 'Не вдалося активувати безкоштовний період. Спробуйте ще раз.',
      daysLeft: 'Залишилось {days} дн.',
      badge: 'Безкоштовно 14 днів',
      coachedBody: 'Поки ви тренуєте з тренером, функції залежать від його плану. Пробний період можна активувати лише після того, як ви залишите тренера.',
    },
    limitReached: {
      clients: 'Оновіться до PRO для необмеженої кількості клієнтів',
      templates: 'Досягнуто ліміт шаблонів. Оновіть план, щоб додати більше шаблонів.',
      workoutMode: 'Режим тренування доступний на Solo або якщо у тренера є Pro.',
      proView: 'ProView доступний на плані Pro.',
      history: 'Історія тренувань доступна на Solo або якщо у тренера є Pro.',
      coachHistory: 'Історія тренувань клієнтів доступна на плані Pro.',
    },
    upgradePrompt: {
      title: 'Оновіть план',
      description: 'Оновіться до Pro для необмеженої кількості клієнтів і інструментів тренера.',
      workoutDescription: 'Відкрийте режим тренування, щоб записувати підходи в залі та зберігати історію.',
      leaveCoachTitle: 'Спочатку залиште тренера',
      leaveCoachToInvite: 'Ви вже тренуєтесь із тренером. Спочатку залиште його, якщо хочете запрошувати власних клієнтів.',
      leaveCoachCta: 'До моїх тренерів',
      workoutDependsOnCoach: 'Ваші функції тренування залежать від плану тренера. Попросіть його про Pro або вийдіть і купіть Solo.',
      proViewDescription: 'Оновіться до Pro, щоб бачити живі сесії та історію тренувань клієнтів.',
      cta: 'Розблокувати',
    },
    feature: {
      autoProlongation: {
        upgradeRequired: 'Оновіться до Pro, щоб увімкнути автопродовження абонементів.',
      },
    },
    locked: {
      createOwn: 'З тренером ви тренуєтесь за його програмами. Власні блоки й програми створювати не можна.',
      soloOnlyTitle: 'Лише для соло-користувачів',
      soloOnlyBody: 'Блоки й програми доступні лише соло-користувачам без тренера. Тренер призначає тренування за вас.',
    },
  },

  // Training Blocks
  trainingBlocks: {
    title: 'Тренувальні блоки',
    subtitle: 'Ваші багаторазові тренувальні блоки',
    loading: 'Завантаження...',
    createBlock: 'Створити блок',
    createInBot: 'Імпорт з тексту',
    createInBotHint: 'Вже маєте збережене тренування в іншому місці? Скопіюйте, вставте або напишіть вільним текстом — ми спробуємо розпізнати блок.',
    editBlock: 'Редагувати',
    copyBlock: 'Дублювати',
    deleteBlock: 'Видалити блок',
    noExercises: 'Немає вправ',
    sets: 'підходи',
    reps: 'повт.',
    exerciseCount_one: '{{count}} вправа',
    exerciseCount_other: '{{count}} вправ(и)',
    emptyState: {
      title: 'Тренувальних блоків ще немає',
      description: 'Створіть перший блок через форму або спробуйте імпорт з тексту.',
    },
    filterAll: 'Всі',
    filterTitle: 'Фільтри',
    filterClear: 'Скинути',
    filterCollapsedHint: 'Натисніть щоб фільтрувати за тегами',
    filterEmpty: 'Немає блоків, що відповідають обраним фільтрам.',
    error: {
      loadFailed: 'Не вдалося завантажити тренувальні блоки',
      copyConfirm: 'Дублювати блок «{{name}}»?',
      copyFailed: 'Не вдалося дублювати блок',
      deleteFailed: 'Не вдалося видалити блок',
      deleteConfirm: 'Видалити блок «{{name}}»?',
      deleteConfirmInUse:
        'Видалити блок «{{name}}»?\n\nВін використовується в: {{programs}}.\nЙого також буде прибрано з цих програм.',
    },
  },

  aiParse: {
    validation: {
      hint: 'Щонайменше {{min}} символів, до {{max}}',
      textRequired: 'Будь ласка, введіть опис',
      textTooShort: 'Введіть щонайменше {{min}} символів для розпізнавання',
      textTooLong: 'Текст має містити не більше {{max}} символів',
      correctionRequired: 'Опишіть, що потрібно виправити',
      correctionTooLong: 'Уточнення має містити не більше {{max}} символів',
      inputTooLarge: 'Занадто великий текст для розпізнавання',
    },
  },

  // Create Training Program From Text
  createTrainingProgramFromText: {
    title: 'Імпорт програми з тексту',
    subtitle: 'Вже маєте збережену програму в іншому місці? Скопіюйте, вставте або напишіть вільним текстом.',
    textareaLabel: 'Опис програми',
    textareaPlaceholder: 'напр., День 1: груди та трицепс — жим лежачи 4x8 80кг, відмикання на брусах 3x12...',
    parseButton: 'Розпізнати',
    parsing: 'Розпізнаємо...',
    programNameLabel: 'Назва програми',
    programNamePlaceholder: 'напр., Поштовх-Тяга-Ноги',
    blockNameLabel: 'Назва блоку',
    confirmButton: 'Створити програму',
    confirming: 'Створення...',
    startOver: 'Почати знову',
    correctionsLabel: 'Потрібні корективи?',
    correctionPlaceholder: 'Опишіть, що виправити, напр. «в день 2 додай більше вправ на ноги»...',
    reparseButton: 'Уточнити',
    reparsing: 'Уточнюємо...',
    attemptsLeft: 'Залишилось уточнень: {{count}}',
    error: {
      parseFailed: 'Не вдалося розпізнати опис програми',
      createFailed: 'Не вдалося створити програму',
      quotaExceeded: 'Ліміт використання ШІ вичерпано. Спробуйте пізніше.',
    },
  },

  // Create Training Block From Text
  createTrainingBlockFromText: {
    title: 'Імпорт блоку з тексту',
    subtitle: 'Вже маєте збережене тренування в іншому місці? Скопіюйте, вставте або напишіть вільним текстом.',
    textareaLabel: 'Опис тренування',
    textareaPlaceholder: 'напр., 3 підходи жим 80кг, 4x10 присідання 100кг, підтягування до відмови...',
    parseButton: 'Розпізнати',
    parsing: 'Розпізнаємо...',
    previewTitle: 'Попередній перегляд',
    previewSubtitle: 'Перевірте розпізнаний блок',
    confirmButton: 'Створити блок',
    confirming: 'Створення...',
    startOver: 'Почати знову',
    correctionsLabel: 'Потрібні корективи?',
    correctionPlaceholder: 'Опишіть, що виправити, напр. «присідання мають бути 5 підходів, а не 4»...',
    reparseButton: 'Уточнити',
    reparsing: 'Уточнюємо...',
    attemptsLeft: 'Залишилось уточнень: {{count}}',
    error: {
      parseFailed: 'Не вдалося розпізнати опис тренування',
      createFailed: 'Не вдалося створити блок',
      quotaExceeded: 'Ліміт використання ШІ вичерпано. Спробуйте пізніше.',
    },
  },

  // Training Tags
  trainingTagSections: {
    muscle: 'Групи м\'язів',
    level: 'Рівень',
    gender: 'Стать',
    type: 'Тип',
  },
  trainingTags: {
    legs: 'Ноги',
    arms: 'Руки',
    shoulders: 'Плечі',
    chest: 'Груди',
    back: 'Спина',
    core: 'Прес',
    cardio: 'Кардіо',
    full_body: 'Все тіло',
    glutes: 'Сідниці',
    calves: 'Литки',
    novice: 'Новачок',
    medium: 'Середній',
    hard: 'Складний',
    advanced: 'Просунутий',
    female: 'Жіночий',
    male: 'Чоловічий',
    unisex: 'Унісекс',
    hit: 'HIT',
    standard: 'Стандарт',
  },

  // Create Training Block
  createTrainingBlock: {
    title: 'Створити тренувальний блок',
    nameLabel: 'Назва блоку',
    namePlaceholder: 'напр., Верхня тяга',
    tagsLabel: 'Теги',
    tagsAdd: 'Додати',
    tagsDone: 'Готово',
    tagsEmptyHint: 'Тегів ще немає',
    tagsRemove: 'Прибрати тег',
    exercisesLabel: 'Вправи',
    addExercise: 'Додати вправу',
    addToGroup: 'Додати до групи',
    groupLabel: 'Група вправ',
    notePlaceholder: 'Примітка (необовʼязково)',
    addNote: 'Додати примітку',
    changeNote: 'Змінити примітку',
    doneNote: 'Готово',
    ungroup: 'Розділити на окремі вправи',
    removeExercise: 'Видалити',
    deleteExercise: 'Видалити вправу',
    moveUp: 'Перемістити вгору',
    moveDown: 'Перемістити вниз',
    exerciseNamePlaceholder: 'Назва вправи',
    setsPlaceholder: 'Підходи',
    repsPlaceholder: 'Повт.',
    weightPlaceholder: 'Вага (напр. 80кг)',
    weightLabel: 'Вага',
    submit: 'Створити блок',
    submitting: 'Створення...',
    orDivider: 'або',
    createInBot: 'Імпорт з тексту',
    createInBotHint: 'Вже маєте збережене тренування в іншому місці? Скопіюйте, вставте або напишіть вільним текстом — ми спробуємо розпізнати блок.',
    error: {
      nameRequired: 'Назва блоку обов\'язкова',
      noExercises: 'Додайте хоча б одну вправу',
      exerciseNameRequired: 'Назва вправи обов\'язкова',
      createFailed: 'Не вдалося створити блок',
    },
  },

  // Edit Training Block
  editTrainingBlock: {
    title: 'Редагувати тренувальний блок',
    viewTitle: 'Тренувальний блок',
    submit: 'Зберегти зміни',
    submitting: 'Збереження...',
    error: {
      loadFailed: 'Не вдалося завантажити тренувальний блок',
      saveFailed: 'Не вдалося зберегти зміни',
    },
  },

  // Training Programs
  trainingPrograms: {
    title: 'Тренувальні програми',
    subtitle: 'Впорядковані послідовності тренувальних блоків',
    loading: 'Завантаження...',
    createProgram: 'Створити програму',
    createFromText: 'Імпорт з тексту',
    createFromTextHint: 'Вже маєте збережену програму в іншому місці? Скопіюйте, вставте або напишіть вільним текстом — ми спробуємо розпізнати програму.',
    editProgram: 'Редагувати',
    deleteProgram: 'Видалити',
    copyProgram: 'Копіювати',
    copyConfirm: 'Копіювати програму «{{name}}»?',
    copyPrefix: 'Копія',
    copyFailed: 'Не вдалося скопіювати програму',
    activeBadge: 'Активна',
    activationHistory: 'Iсторія',
    blockCount_one: '{{count}} блок',
    blockCount_other: '{{count}} блоків',
    blockIndex: 'Блок {{n}}',
    emptyState: {
      title: 'Програм ще немає',
      description: 'Програма — це впорядкована послідовність тренувальних блоків для вашого розкладу.',
    },
    filterEmpty: 'Немає програм, що відповідають обраним фільтрам.',
    error: {
      loadFailed: 'Не вдалося завантажити тренувальні програми',
      deleteFailed: 'Не вдалося видалити програму',
      deleteConfirm: 'Видалити програму «{{name}}»?',
    },
    activate: {
      title: 'Активувати програму',
      forMyselfButton: 'Активувати для себе',
      durationLabel: 'Тривалість',
      durationWeeks: '{{count}} тиж.',
      startButton: 'Запустити програму',
      starting: 'Запуск...',
      noScheduleWarning: 'Розклад тренувань не встановлено. Додайте тижневий розклад щоб продовжити.',
      goToSchedule: 'Налаштувати розклад',
      successMessage: 'Програму активовано!',
      error: {
        activateFailed: 'Не вдалося активувати програму',
        deactivateFailed: 'Не вдалося деактивувати програму',
      },
    },
    deactivate: 'Деактивувати',
    assignProgram: {
      title: 'Призначити програму',
      assignButton: 'Призначити клієнту',
      assigning: 'Призначення...',
      deassignButton: 'Скасувати призначення',
      selectProgram: 'Оберіть програму',
      searchPlaceholder: 'Пошук програм…',
      noPrograms: 'Програм немає',
      noSearchResults: 'Нічого не знайдено',
      selectTrainee: 'Оберіть клієнта',
      noTrainees: 'Клієнтів немає',
      assignedTrainees: 'Призначено клієнтам',
      fromCoachBadge: 'Від тренера',
      currentAssignment: 'Поточне призначення',
      scheduleLabel: 'Період',
      startDate: 'Дата початку',
      endDate: 'Дата завершення',
      quickDuration: 'Тривалість',
      durationWeeks: '{{count}} тиж.',
      weekChip: '{{count}}т',
      endBeforeStart: 'Дата завершення має бути не раніше дати початку',
      error: {
        assignFailed: 'Не вдалося призначити програму',
        deassignFailed: 'Не вдалося скасувати призначення',
      },
      onDemand: {
        cta: 'Створити на вимогу',
        ctaHint: 'Одноразова програма під цього клієнта',
        title: 'Програма на вимогу',
        daysLabel: 'Блоки',
        pickBlock: 'Обрати збережений блок',
        createBlock: 'Створити новий блок',
        addBlock: 'Додати блок',
        removeDay: 'Прибрати',
        fromLibrary: 'З бібліотеки',
        newBlockBadge: 'Новий (не в бібліотеці)',
        noSavedBlocks: 'Збережених блоків ще немає',
        defaultBlockName: 'Блок {{n}}',
        error: {
          daysRequired: 'Додайте хоча б один блок',
          exerciseRequired: 'Додайте хоча б одну вправу з назвою',
        },
      },
    },
    viewProjections: 'Переглянути розклад',
    projections: {
      title: 'Розклад програми',
      sessions: '{{count}} тренувань',
      weeks: '{{count}} тиж.',
      empty: 'Тренувань не заплановано',
      cancelledBadge: 'Скасовано',
    },
    history: {
      title: 'Історія активацій',
      empty: 'Історія активацій порожня',
      endedLabel: 'Завершено',
      ongoingLabel: 'Поточна',
      startedLabel: 'Розпочато',
      saveToLibrary: 'Додати до збережених',
      saving: 'Додавання...',
      saved: 'Збережено',
      unsavedRetentionHint: 'Не збережені програми старші за 3 місяці видаляються з історії. Додайте їх до збережених, щоб залишити.',
    },
    saveToLibrary: 'Зберегти в бібліотеку',
    saveBlockToLibrary: 'Зберегти блок',
    savingToLibrary: 'Збереження...',
  },

  // Create / Edit Training Program
  createTrainingProgram: {
    title: 'Створити програму',
    editTitle: 'Редагувати програму',
    nameLabel: 'Назва програми',
    namePlaceholder: 'напр., Поштовх-Тяга-Ноги',
    blocksLabel: 'Послідовність блоків',
    saving: 'Збереження...',
    submit: 'Створити програму',
    submitEdit: 'Зберегти зміни',
    noBlocksTitle: 'Тренувальних блоків немає',
    noBlocksDescription: 'Спочатку створіть блоки командою',
    noBlocksDescriptionSuffix: 'у боті.',
    addBlock: 'Додати блок',
    selectBlock: 'Оберіть блок',
    changeBlock: 'Змінити',
    addNBlocks: 'Додати {{count}} блок(и)',
    currentBlock: 'Поточний блок',
    loading: 'Завантаження...',
    pickSavedBlock: 'Обрати збережений блок',
    createNewBlock: 'Створити новий блок',
    newBlockBadge: 'Новий',
    noSavedBlocks: 'Збережених блоків ще немає',
    defaultBlockName: 'Блок {{n}}',
    createFromText: 'Імпорт з тексту',
    createFromTextHint: 'Вже маєте збережену програму в іншому місці? Скопіюйте, вставте або напишіть вільним текстом — ми спробуємо розпізнати програму.',
    error: {
      nameRequired: 'Назва програми обов\'язкова',
      noBlocksSelected: 'Додайте хоча б один блок',
      exerciseRequired: 'Додайте хоча б одну вправу з назвою',
      loadBlocksFailed: 'Не вдалося завантажити тренувальні блоки',
      createFailed: 'Не вдалося створити програму',
      updateFailed: 'Не вдалося оновити програму',
      loadFailed: 'Не вдалося завантажити програму',
    },
  },

  // Preferences
  preferences: {
    title: 'Налаштування',
    subtitle: 'Керування вашими налаштуваннями',
    backToDashboard: 'Повернутися до головної',
    training: {
      templates: {
        title: 'Шаблони подій',
        description: 'Створюйте та керуйте шаблонами абонементів',
      },
    },
    userProfile: {
      title: 'Мій профіль',
      description: 'Переглянути ім’я, фото та нікнейм',
      firstName: "Ім'я",
      lastName: 'Прізвище',
      username: "Нікнейм",
      noInfo: 'Немає даних профілю',
      photoSyncEnabled: 'Синхронізація фото',
      photoSyncDescription: 'Синхронізувати фото профілю з Telegram у додаток. Синхронізація відбувається протягом дня, тому оновлення тут може зайняти деякий час після зміни фото в Telegram.',
      syncPhotoNow: 'Синхронізувати фото зараз',
      syncingPhoto: 'Синхронізація...',
      syncPhotoSuccess: 'Фото успішно синхронізовано!',
      syncPhotoError: 'Не вдалося синхронізувати фото. Спробуйте ще раз.',
      syncPhotoDisabled: 'Спочатку увімкніть синхронізацію фото.',
      syncPhotoFailedWithPrivacy: 'Не вдалося синхронізувати фото. Перевірте налаштування конфіденційності Telegram і переконайтеся, що ваше фото профілю видиме для всіх. Перейдіть до Налаштування Telegram > Конфіденційність і безпека > Фото профілю та встановіть "Усі".',
    },
    notifications: {
      title: 'Сповіщення',
      description: 'Оберіть, які сповіщення ви хочете отримувати',
      traineeSection: 'Як клієнт',
      coachSection: 'Як тренер',
      myLastTrainingLeft: 'Нагадування про останнє тренування в абонементі',
      traineesLastTrainingLeft: 'Нагадування про останнє тренування в абонементах клієнтів',
      trainingsCanBeCompleted: 'Нагадування про абонементи, які можна завершити',
    },
    trainingSchedule: {
      title: 'Розклад тренувань',
      description: 'Вкажіть дні та час ваших тренувань',
      scheduleLabel: 'Дні тренувань',
      daysPerWeek: 'дн. / тиждень',
      tapToAdjustTime: 'Натисніть на час для зміни',
      emptyTitle: 'Дні тренувань не вибрано',
      emptyHint: 'Натисніть на будь-який день вище, щоб додати його до розкладу',
      save: 'Зберегти розклад',
      saving: 'Збереження...',
      saveSuccess: 'Розклад збережено',
      error: {
        loadFailed: 'Не вдалося завантажити розклад',
        saveFailed: 'Не вдалося зберегти розклад',
      },
    },
    support: {
      title: 'Повідомити про проблему',
      description: 'Надішліть звіт про помилку або проблему',
    },
    saveSuccess: 'Налаштування успішно збережено',
    saveError: 'Не вдалося зберегти налаштування',
  },

  support: {
    title: 'Повідомити про проблему',
    subtitle: 'Опишіть, що пішло не так. Ми переглянемо проблему якомога швидше.',
    categoryLabel: 'Яка це проблема?',
    categories: {
      bug: {
        label: 'Помилка',
        description: 'Щось виглядає неправильно або працює неочікувано',
      },
      blocking: {
        label: 'Блокуюча проблема',
        description: 'Не можу користуватися основною функцією',
      },
    },
    messageLabel: 'Опишіть проблему',
    messagePlaceholder: 'Що сталося? Що ви очікували?',
    submit: 'Надіслати звіт',
    submitting: 'Надсилання…',
    success: 'Звіт надіслано. Дякуємо!',
    successHint: 'Ми отримали ваш звіт і переглянемо його якомога швидше.',
    usernameMissing: {
      title: 'Потрібен Telegram нікнейм',
      message: 'У вас немає Telegram нікнейма, тому підтримка не зможе зв’язатися з вами, якщо знадобиться уточнення.',
      details: 'Відкрийте Налаштування Telegram → Нікнейм, встановіть нікнейм, потім знову відкрийте додаток і спробуйте ще раз.',
    },
    errors: {
      validation: 'Перевірте повідомлення та спробуйте ще раз.',
      rateLimited: 'Зачекайте кілька хвилин перед наступним звітом.',
      submitFailed: 'Не вдалося надіслати звіт. Спробуйте ще раз.',
    },
  },

  workout: {
    startTitle: 'Почати тренування',
    startSubtitle: 'Оберіть блок і тренуйтесь з історією попередніх результатів.',
    viewProgram: 'Переглянути програму',
    freePlan: {
      title: 'Ваша програма',
      subtitle: 'Дивіться поточну програму і тренуйтесь за нею в залі.',
      bannerTitle: 'Безкоштовний план',
      bannerBody:
        'На безкоштовному плані ви бачите поточну програму і можете тренуватись за нею. Щоб відстежувати прогрес під час тренування і зберігати історію, потрібен план Solo.',
      upgradeCta: 'Розблокувати',
      noProgram: 'Активуйте програму, щоб бачити її тут і тренуватись за нею.',
      goToPrograms: 'До програм',
    },
    startBlock: 'Почати',
    loading: 'Завантаження…',
    activeProgram: 'Активна програма',
    assignedByCoach: 'Призначено вашим тренером',
    programBlocks: 'Блоки програми',
    chooseSavedBlock: 'Оберіть збережений блок',
    exerciseCount: '{{count}} вправ',
    noExercises: 'Немає вправ',
    noBlocksInProgram: 'У цій програмі ще немає блоків.',
    noSavedBlocks: 'У вас ще немає збережених блоків.',
    goToBlocks: 'До блоків',
    unlockCta: 'Розблокувати',
    resumeTitle: 'Тренування триває',
    resumeSubtitle: 'Продовжити {{block}}',
    resumeCta: 'Повернутися',
    sessionTitle: 'Тренування',
    progress: '{{current}} / {{total}}',
    progressGroups: '{{current}} / {{total}} груп',
    previousExercise: 'Попередня',
    nextExercise: 'Далі',
    navHint: 'Перемикайте вправи стрілками на картці або картками зверху й знизу.',
    navHintGroups: 'Перемикайте групи вправ стрілками на картці.',
    plannedSets: 'Підходи',
    plannedReps: 'Повтори',
    plannedWeight: 'Вага',
    targetLabel: 'План',
    setsLabel: 'Підходи',
    setWeight: 'Вага',
    setReps: 'Повтори',
    setWeightLabel: 'Підхід {n} вага',
    setRepsLabel: 'Підхід {n} повтори',
    addSet: 'Додати підхід',
    removeSet: 'Прибрати',
    commentLabel: 'Коментар',
    commentToggleHint: 'Необов’язкова нотатка до вправи',
    commentPlaceholder: 'Відчуття, підказки, нотатки…',
    blockNoteLabel: 'Примітка блоку',
    addWorkoutNote: 'Додати нотатку',
    changeWorkoutNote: 'Змінити нотатку',
    doneNote: 'Готово',
    logOptionalHint: 'Запис необов’язковий — натисніть «Далі», коли будете готові.',
    historyTitle: 'Попередні рази',
    historyEmpty: 'Для цієї вправи ще немає записів.',
    historyNoActuals: 'Завершено (без чисел)',
    historySets: 'Підходи',
    historyComment: 'Нотатка',
    historySetsHint: 'Вага × повтори для кожного записаного підходу.',
    historyShowMore: 'Показати ще {{count}}',
    historyShowLess: 'Показати менше',
    repsSuffix: 'повт.',
    prev: 'Назад',
    next: 'Далі',
    finish: 'Завершити',
    finishing: 'Завершення…',
    cancel: 'Скасувати тренування',
    canceling: 'Скасування…',
    cancelConfirm: 'Скасувати це тренування? Увесь прогрес буде видалено і не потрапить в історію.',
    backToStart: 'На початок',
    error: {
      loadFailed: 'Не вдалося завантажити режим тренування',
      startFailed: 'Не вдалося почати тренування',
      loadSessionFailed: 'Не вдалося завантажити сесію',
      saveFailed: 'Не вдалося зберегти запис',
      finishFailed: 'Не вдалося завершити тренування',
      cancelFailed: 'Не вдалося скасувати тренування',
    },
    conflict: {
      log: 'Цю вправу вже оновив хтось інший. Показано актуальні значення — змініть їх знову, щоб зберегти свої.',
      session: 'Тренування змінилось, поки ви перемикали вправи. Показано актуальний стан.',
    },
  },

  quickAccess: {
    title: 'Швидкий доступ',
    subtitle: 'Ваші ярлики на головному екрані',
    editHint: 'Змінити',
    saveButton: 'Зберегти',
    selectHint: 'Оберіть до 3 розділів для швидкого доступу',
    saveSuccess: 'Ярлики збережено',
    saveError: 'Не вдалося зберегти ярлики',
    items: {
      'this-week-trainings': 'Розклад тренувань',
      'all-training-sets': 'Абонементи',
      'active-training-set': 'Абонемент',
      'training-blocks': 'Збережені тренувальні блоки',
      'training-programs': 'Збережені тренувальні програми',
      'training-schedule': 'Розклад власних тренувань',
      'start-training': 'Почати тренування',
      'program-history': 'Моя iсторія',
      'pro-view': 'Pro View',
      'trainees': 'Клієнти',
      'templates': 'Шаблони абонементів',
      'invite': 'Запросити клієнта',
      'my-coaches': 'Мої тренери',
      'become-specialist': 'Стати тренером',
      'my-profile': 'Мій профіль',
      'subscription': 'Підписка',
      'support': 'Повідомити про проблему',
      'notifications-settings': 'Сповіщення',
      'quick-access': 'Швидкий доступ',
    },
  },

  historyPage: {
    title: 'Історія',
    clientTitle: 'Історія · {{name}}',
    tabs: {
      sets: 'Абонементи',
      programs: 'Програми',
      workouts: 'Тренування',
    },
    workouts: {
      empty: 'Ще немає завершених тренувань',
      entitlementRequired: 'Розблокуйте режим тренувань, щоб зберігати історію.',
      unlockTitle: 'Зберігайте історію тренувань',
      unlockBody:
        'На безкоштовному плані завершені тренування не зберігаються з прогресом. Розблокуйте Solo, щоб записувати заняття й бачити, як ви прогресуєте.',
      unlockCta: 'Розблокувати',
      coachPath:
        'Якщо ви тренуєтеся з тренером, він має надіслати вам запрошення. Тренер на Pro відкриває історію тренувань для вас.',
      unnamedBlock: 'Тренування',
      loadingDetail: 'Завантаження…',
      planned: 'План',
      deleteAria: 'Видалити тренування',
      deleteConfirm: 'Видалити тренування «{{name}}»? Цю дію не можна скасувати.',
      deleteFailed: 'Не вдалося видалити тренування',
    },
    error: {
      loadFailed: 'Не вдалося завантажити історію',
      clientNotFound: 'Клієнта не знайдено',
    },
  },

  tour: {
    coach: {
      skip: 'Пропустити',
      next: 'Далі',
      done: 'Готово',
      welcome: {
        title: 'Тур для тренера',
        startTour: 'Показати інтерфейс',
        slides: [
          {
            title: 'Вітаємо, тренере',
            body: 'Короткий огляд місць, якими ви користуватиметеся щодня з клієнтами.',
          },
          {
            title: 'Плануйте й призначайте',
            body: 'Створюйте абонементи, блоки та програми — і призначайте їх клієнтам.',
          },
          {
            title: 'Тримайте розклад',
            body: 'Переглядайте тиждень, проводьте тренування та керуйте клієнтами в розділі Більше.',
          },
        ],
      },
      steps: {
        todayTitle: 'Тренування сьогодні',
        todayBody: 'День одним поглядом — натисніть, щоб відкрити розклад на сьогодні.',
        clientsTitle: 'Ваші клієнти',
        clientsBody: 'Запрошуйте клієнтів або швидко відкривайте когось із цієї стрічки.',
        moreTitle: 'Клієнти та коучинг',
        moreBody: 'Клієнти, шаблони, Pro View та інші інструменти тренера — тут.',
        assistantTitle: 'Потрібна допомога?',
        assistantBody: 'Якщо лишилися питання — спробуйте нашого асистента.',
      },
    },
    traineeSolo: {
      skip: 'Пропустити',
      next: 'Далі',
      done: 'Готово',
      welcome: {
        title: 'Тур для тих хто завітав вперше',
        startTour: 'Показати інтерфейс',
        slides: [
          {
            title: 'Вітаємо',
            body: 'Тренуйтеся самостійно — створіть програму, щоб почати тренування.',
          },
          {
            title: 'Ваші інструменти',
            body: 'Головна, Швидкий доступ і Більше — планування й тренування під рукою.',
          },
        ],
      },
      steps: {
        homeTitle: 'Ваша головна',
        homeBody: 'Створіть тренувальну програму, щоб почати тренування. Прогрес з’явиться на цій картці.',
        quickAccessTitle: 'Швидкий доступ',
        quickAccessBody: 'Закріпіть інструменти, якими користуєтеся найчастіше.',
        personalTitle: 'Особисті тренування',
        personalBody: 'Блоки, програми, старт тренування та історія — тут.',
        createBlockTitle: 'Створити блок',
        createBlockBody: 'Почніть тут — кожен блок це шаблон тренування.',
        createProgramTitle: 'Створити програму',
        createProgramBody: 'Зберіть блоки в програму й активуйте її, щоб тренуватися.',
        accountTitle: 'Акаунт',
        accountBody: 'Профіль, підписка, сповіщення та налаштування швидкого доступу.',
        becomeCoachTitle: 'Хочете тренувати інших?',
        becomeCoachBody: 'Натисніть «Стати тренером», коли будете готові запрошувати клієнтів.',
        assistantTitle: 'Потрібна допомога?',
        assistantBody: 'Якщо лишилися питання — спробуйте нашого асистента.',
      },
    },
    traineeWithCoach: {
      skip: 'Пропустити',
      next: 'Далі',
      done: 'Готово',
      welcome: {
        title: 'Тренування з тренером',
        startTour: 'Показати інтерфейс',
        slides: [
          {
            title: 'У вас є тренер',
            body: 'Тренер може призначати абонементи і тренувальні програми — ось що змінилося.',
          },
          {
            title: 'Будьте в синхроні',
            body: 'Тренер і абонемент — на Головній. Старт тренування та історія — у розділі «Тренування з тренером» у Більше.',
          },
        ],
      },
      steps: {
        setTitle: 'Ваш абонемент',
        setBody: 'Тут ваш абонемент — тренер, прогрес і тренування з’являться після призначення.',
        withCoachTitle: 'Тренування з тренером',
        withCoachBody: 'Активний абонемент, тренування цього тижня, старт і історія — все тут.',
        assistantTitle: 'Потрібна допомога?',
        assistantBody: 'Є питання — спробуйте асистента.',
      },
    },
    trainingSetDetailCoach: {
      skip: 'Пропустити',
      next: 'Далі',
      done: 'Готово',
      steps: {
        overviewTitle: 'Абонемент',
        overviewBody: 'Тут прогрес, найближчі тренування та дії з абонементом.',
        progressTitle: 'Прогрес',
        progressBody: 'Завершені, пропущені й залишені тренування. Маркер показує, де зараз клієнт.',
        completeTitle: 'Завершити',
        completeBody: 'Закрийте абонемент, коли всі тренування закінчаться. Можна одразу почати наступний.',
        reportTitle: 'Звіт',
        reportBody: 'Надішліть підсумок цього абонемента в Telegram.',
        unpaidTitle: 'Оплата',
        unpaidBody: 'Позначте абонемент як неоплачений, щоб не забути про борг.',
        pauseTitle: 'Пауза',
        pauseBody: 'Заморозьте абонемент на період — тренування продовжаться після паузи.',
        resumeTitle: 'Відновити',
        resumeBody: 'Завершіть паузу раніше й продовжте розклад.',
        cancelPauseTitle: 'Скасувати паузу',
        cancelPauseBody: 'Приберіть заплановану паузу, якщо вона більше не потрібна.',
        deleteTitle: 'Видалити',
        deleteBody: 'Видаліть абонемент. Це не можна скасувати.',
      },
    },
    trainingSetDetailTrainee: {
      skip: 'Пропустити',
      next: 'Далі',
      done: 'Готово',
      steps: {
        overviewTitle: 'Абонемент',
        overviewBody: 'Тут ваш прогрес і найближчі тренування. Керує абонементом тренер.',
        progressTitle: 'Прогрес',
        progressBody: 'Завершені, пропущені й залишені тренування. Маркер показує, де ви зараз.',
      },
    },
  },
};
